/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import _ from 'lodash';
import { intcomma, apmonth, apdate, pluralize } from 'journalize';
import {
  movingAvg,
  customDateFormat,
  customMobileDateFormat,
  customNumFormat,
  getLabeledData,
  mergeArrays,
} from '../utils/charts-utils';
import * as moment from 'moment';
import { wordwrap } from 'd3-jetpack';
// import { swoopyDrag } from '../swoopy-drag';

import data from '../../../data/daily_tables/all_tables.json';
import colors from '../../../data/chart-colors.json';

// console.log(moment('4_14_2020 4:00 pm', 'M_D_YYYY h:mm a').utc().format())

// a reference to the default graphic container, change if needed
const container = d3.select('#new-cases');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

//set margins
let margin = {
  top: 15,
  left: 55,
  right: 45,
  bottom: 40,
};

let breakPoint = 500;

// grabbing totals
let dataTotals = [];

// there are six properties prior that we want to exclude: 'latest_date', 'yesterday_date', 'week_ago', 'cases_latest_date', 'cases_yesterday_date', 'cases_week_ago', 'cases_yesterday_date'
for (let i = Object.keys(data).length - 1; i > 6; i--) {
  var case_dif = data[Object.keys(data)[i - 1]].statewide_data.case_dif;

  if (
    moment(Object.keys(data)[i], 'M_D_YYYY').isBefore(
      moment(data['cases_latest_date'], 'M_D_YYYY')
    )
  ) {
    dataTotals.push({
      unformatted_date: Object.keys(data)[i - 1],
      new_cases: case_dif ? case_dif : 0,
      incomplete: data[Object.keys(data)[i - 1]].statewide_data.incomplete,
    });
  }
}

// changing to moment date+time objects
dataTotals.forEach(d => (d.date = moment(d.unformatted_date, 'M_D_YYYY')));

// Add an id based on index to use for joining
dataTotals.forEach((d, i) => (d.id = i));

// Make object into array
let dataArray = dataTotals.map(d => d['new_cases']);

let dataMovingAvg = movingAvg(dataArray, 7, undefined, 'moving_avg');

dataTotals = mergeArrays(dataTotals, dataMovingAvg, 'id');

// grabbing totals and ranges
let dateRange = d3.extent(dataTotals, d => d.date),
  totalsMax = d3.max(dataTotals, d => d.new_cases);

// set ranges
let x = d3.scaleTime().domain(dateRange);
let y = d3
  .scaleLinear()
  .domain([0, totalsMax])
  .nice();

// append svg
let svg = container.append('svg'),
  g = svg.append('g').attr('class', 'line-chart'),
  labels_g = svg.append('g').attr('class', 'labels-container');

let xAxis = g.append('g').attr('class', 'axis axis--x'),
  yAxis = g.append('g').attr('class', 'axis axis--y');

let line = d3
  .line()
  .defined(d => !isNaN(d.moving_avg))
  .x(d => x(d.date))
  .y(d => y(d.moving_avg));

let bars = g
  .selectAll('.bar')
  .data(dataTotals)
  .enter()
  .append('rect')
  .attr('class', 'bar')
  .attr('fill', d => {
    if (d.incomplete && d.incomplete.includes('case_dif'))
      // color incomplete bars gray
      return colors.bar_neutral;
    else
      return d.id == dataTotals.length - 1
        ? colors.bar_highlight
        : colors.bar_default;
  })
  .attr('opacity', 1);

let dateReopening = moment('5_01_2020', 'M_D_YYYY');
let dateMaskOrder = moment('7_02_2020', 'M_D_YYYY');

let reopeningGroup = g.append('g').attr('class', 'reopening-line');
let reopeningLine = reopeningGroup
  .append('line')
  .attr('x1', 0)
  .attr('x2', 0)
  .attr('y1', 0)
  .attr('stroke', '#797979')
  .attr('stroke-width', '1px')
  .attr('stroke-dasharray', '3px');
let reopeningText = reopeningGroup.append('text');

let maskorderGroup = g.append('g').attr('class', 'reopening-line');
let maskorderLine = maskorderGroup
  .append('line')
  .attr('x1', 0)
  .attr('x2', 0)
  .attr('y1', 0)
  .attr('stroke', '#797979')
  .attr('stroke-width', '1px')
  .attr('stroke-dasharray', '3px');
let maskorderText = maskorderGroup.append('text');

dataTotals.filter(line.defined());

let pathOutline = g
  .append('path')
  .data([dataTotals])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', '#fff')
  .attr('stroke-width', 4);

let path = g
  .append('path')
  .data([dataTotals])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', colors.line_moving_avg)
  .attr('stroke-width', 2);

const day_for_label = [[dataTotals][0][6]]; // Gets the day that where the 7-day average label will be next to

let moving_avg_label = g.append('g').attr('class', 'moving-avg-label');

let moving_avg_g = moving_avg_label
  .selectAll('g')
  .data(day_for_label)
  .enter()
  .append('g');

let moving_avgText = moving_avg_g.append('g').attr('class', 'label-text');

moving_avgText
  .append('text')
  .text('7-day average')
  .attr('fill', colors.line_moving_avg)
  .attr('font-weight', 700);

let labels = labels_g.append('g').attr('class', 'labeled-elements');

let labelElements = labels
  .selectAll('g')
  .data(getLabeledData(dataTotals, ['']))
  .enter()
  .append('g')
  .attr('class', 'label-element');

let labelText = labelElements.append('g').attr('class', 'label-text');

labelText
  .append('text')
  .text((d, i) => {
    if (i === 0) {
      let month = apmonth(new Date(d.date)),
        day = d.date.format('D');

      return `${month} ${day}`;
    } else return '';
  })
  .attr('font-weight', 700);

let casesLabel = labelText.append('text').text(d => {
  return `${intcomma(d.new_cases)} case${pluralize(d.new_cases)}`;
});

function resize() {
  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth < breakPoint) {
    frameHeight = 275;
    margin.right = 75;
  } else {
    frameHeight = 325;
    margin.right = 95;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );
  labels_g.attr('transform', `translate(${margin.left},${margin.top})`);

  let barWidth = chartWidth / dataTotals.length;

  x.range([barWidth / 1.5, chartWidth - barWidth / 1.5]);
  y.range([chartHeight, 0]);

  bars
    .attr('width', barWidth - barWidth / 5)
    .attr('x', d => x(d.date) - barWidth / 2)
    .attr('y', d => y(d.new_cases))
    .attr('height', d => chartHeight - y(d.new_cases));

  xAxis
    .call(
      d3
        .axisBottom(x)
        .ticks(15)
        .tickFormat((d, i) => {
          return frameWidth < breakPoint
            ? customMobileDateFormat(d, i)
            : customDateFormat(d, i, 2);
        })
    )
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(5)
      .tickSize(-chartWidth)
      .tickFormat(customNumFormat)
  );

  pathOutline.attr('d', line);
  path.attr('d', line);

  moving_avg_g
    .attr('transform', d => `translate(${x(d.date)},${y(d.moving_avg)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  moving_avgText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(0, -11)';
      } else return 'translate(0, -11)';
    })
    .attr('text-anchor', 'start');

  labelElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.new_cases)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  labelText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(9, 0)';
      } else return 'translate(15, 0)';
    })
    .attr('text-anchor', 'start');

  casesLabel.attr('dy', frameWidth < breakPoint ? 16 : 17);

  reopeningGroup.attr('transform', `translate(${x(dateReopening)}, 0)`);
  reopeningLine.attr('y2', chartHeight);
  reopeningText
    .attr('y', frameWidth < breakPoint ? 0 : 13)
    .text('')
    .tspans(wordwrap('Reopening began May 1', 15))
    .attr('dx', 5)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  maskorderGroup.attr('transform', `translate(${x(dateMaskOrder)}, 0)`);
  maskorderLine.attr('y2', chartHeight);
  maskorderText
    .attr('y', frameWidth < breakPoint ? 75 : 95)
    .attr('text-anchor', 'end')
    .text('')
    .tspans(wordwrap('Mask order issued July 2', 15))
    .attr('dx', -5)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');
}

// call resize on load
resize();
window.addEventListener('resize', _.debounce(resize, 200));

// OUTSIDE OF RESIZE()

// let events = g.append('g').attr('class', 'events');

// let eventGroups = events
//   .selectAll('g')
//   .data([
//     {
//       event_desktop: 'San Antonio opens its first drive-thru testing site',
//       event_mobile: 'Bush',
//       date: '3_13',
//     },
//   ])
//   .enter()
//   .append('g');

// let eventText = eventGroups.append('text');

// let eventLines = eventGroups
//   .append('line')
//   .attr('stroke', '#d1d1d1')
//   .attr('stroke-width', '1px')
//   .attr('stroke-dasharray', 5);

// let metaInfo = g
//   .append('g')
//   .attr('class', 'meta')
//   .attr('font-size', '12px')
//   .attr('fill', 'hsl(0, 0%, 47%)');

// this is the same function findChartMeta() in nunjucks.js
// find a way to import that function here
// metaInfo
//   .append('text')
//   .text(`Note: ${data.Meta.find(d => d.Chart === 'Line').Note}`)
//   .attr('y', 45);

// metaInfo
//   .append('text')
//   .html(
//     `Source: <a style="fill: hsl(0, 0%, 47%); text-decoration: underline;"href="https://www.cbp.gov/sites/default/files/assets/documents/2019-Mar/bp-total-monthly-apps-sector-area-fy2018.pdf">${
//       data.Meta.find(d => d.Chart === 'Line').Source
//     }</a>`
//   )
//   .attr('y', 65);

//arrow marker, need to append to the svg first to use
// svg
//   .append('marker')
//   .attr('id', 'arrow')
//   .attr('viewBox', '-10 -10 20 20')
//   .attr('markerWidth', 10)
//   .attr('markerHeight', 10)
//   .attr('fill', 'hsl(0, 0%, 29%)')
//   .attr('orient', 'auto-start-reverse')
//   .append('path')
//   .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

// let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

// //annotations for swoopyDrag
// let annotations = [
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: 'M-25,-19C-32,-38,-47,-49,-63,-50',
//     text: 'March 24',
//     weight: 700,
//     textOffset: [-186, -77],
//   },
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: '',
//     text: 'State changed how it reports numbers.',
//     weight: 400,
//     textOffset: [-186, -63],
//   },
// ];

// let mobileAnnotations = [
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: 'M-12,-13C-30,-13,-42,-24,-46,-39',
//     text: 'March 24',
//     weight: 700,
//     textOffset: [-105, -75],
//   },
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: '',
//     text: 'State changed how it reports numbers.',
//     weight: 400,
//     textOffset: [-105, -62],
//   },
// ];

// INSIDE OF RESIZE()

// eventGroups.attr(
//   'transform',
//   d => `translate(${(0, x(moment(d.date, 'M_D')))})`
// );

// eventLines.attr('y1', 5).attr('y2', chartHeight);

// eventText
//   .text(d => (frameWidth < breakPoint ? d.event_mobile : d.event_desktop))
//   .attr('font-size', frameWidth < breakPoint ? '12px' : '14px')
//   .attr('dx', frameWidth < breakPoint ? 5 : 10)
//   .attr('dy', frameWidth < breakPoint ? 14 : 20);

// metaInfo.attr('transform', `translate(0, ${chartHeight})`);

// let swoopy = swoopyDrag()
//   .x(d => x(d.xVal) + (frameWidth < breakPoint ? 5 : 20))
//   .y(d => y(d.yVal) + 35)
//   .draggable(false)
//   .annotations(frameWidth < breakPoint ? mobileAnnotations : annotations);

// swoopySel
//   .attr('transform', `translate(${margin.left},${margin.top})`)
//   .call(swoopy)
//   .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

// swoopySel.selectAll('text').each(function(d) {
//   d3.select(this)
//     .text('')
//     .attr('font-weight', d.weight)
//     .tspans(wordwrap(d.text, frameWidth < breakPoint ? 24 : 24));
// });

// swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');
